import classNames from 'classnames';
import type { FunctionComponent } from 'react';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { isMemberLoggedIn } from '@trello/authentication';
import { e2bId } from '@trello/config';
import { BoardIcon } from '@trello/nachos/icons/board';
import { PowerUpIcon } from '@trello/nachos/icons/power-up';
import { Spinner } from '@trello/nachos/spinner';
import { dangerouslyConvertPrivacyString } from '@trello/privacy';
import { navigate } from '@trello/router/navigate';
import type { PowerUpTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';
import { escapeReturnUrl } from '@trello/urls';
import {
  useWorkspaceNavigation,
  useWorkspaceNavigationHidden,
} from '@trello/workspace-navigation';

import { BUTLER_POWER_UP_ID } from 'app/scripts/data/butler-id';
import { useDocumentTitle } from 'app/src/components/DocumentTitle';
import { Footer } from 'app/src/components/Footer';
import { Navigation } from 'app/src/components/Navigation';
import { AllPage } from './AllPage';
import { CategoryPage } from './CategoryPage';
import { EnablePage } from './EnablePage';
import { getPluginsByTag, HomePage } from './HomePage';
import { ListingPage } from './ListingPage';
import { MadeByTrelloPage } from './MadeByTrelloPage';
import { useMemberLocaleQuery } from './MemberLocaleQuery.generated';
import { NavigationFooter } from './NavigationFooter';
import { usePowerUpDirectoryCategoriesQuery } from './PowerUpDirectoryCategoriesQuery.generated';
import { usePublicPluginsQuery } from './PublicPluginsQuery.generated';
import { SearchPage } from './SearchPage';
import { SearchWrapper } from './SearchWrapper';
import type { Plugin, Plugins } from './types';
import { useSharedStrings } from './useSharedStrings';

import styles from './PowerUpDirectory.less';

export const BUTLER_HELP_URL =
  'https://help.trello.com/article/1198-an-intro-to-butler';

export const getPluginsByCategory = (plugins: Plugins, category: string) =>
  plugins.filter((plugin: Plugin) => plugin.categories.includes(category)) ||
  [];

export const isValidCategory = (idCategory: string, categories: string[]) =>
  categories.includes(idCategory);

export const getPluginById = (plugins: Plugins, idPlugin?: string) =>
  plugins.find((plugin: Plugin) => plugin.id === idPlugin) || null;

export const filterUnavailablePlugins = (
  plugins: Plugins,
  options = { filterDeprecated: true },
) => {
  return plugins.filter((plugin) => {
    if (
      plugin.id === e2bId ||
      ['moderated', 'hidden'].includes(plugin.moderatedState ?? '')
    ) {
      return false;
    }

    if (options.filterDeprecated) {
      return !plugin.deprecation?.sunsetDate;
    }

    return true;
  });
};

const LoadingSpinner = () => (
  <div
    className={classNames(
      styles.directoryBody,
      styles.directorySpinnerContainer,
    )}
  >
    <Spinner testId={getTestId<PowerUpTestIds>('pup-directory-loading')} />
  </div>
);

interface PowerUpDirectoryProps {
  section?: string | null;
  subsection?: string | null;
  query?: string;
  orgId?: string;
}

export const PowerUpDirectory: FunctionComponent<PowerUpDirectoryProps> = ({
  section,
  subsection,
  query,
}) => {
  const intl = useIntl();
  const { getCategoryMessage } = useSharedStrings();
  const { data: memberLocaleQuery, loading: loadingMemberLocale } =
    useMemberLocaleQuery();

  const { data, error, loading } = usePublicPluginsQuery({
    variables: {
      preferredLocales:
        dangerouslyConvertPrivacyString(
          memberLocaleQuery?.member?.prefs?.locale,
        ) || 'en',
    },
    waitOn: ['MemberHeader'],
  });
  const { data: categoriesQuery, loading: loadingCategories } =
    usePowerUpDirectoryCategoriesQuery();

  const [navigationExpanded, setNavigationExpanded] = useState(false);

  const [{ expanded: _workspaceNavigationExpanded }] = useWorkspaceNavigation();
  const [{ hidden: _workspaceNavigationHidden }] =
    useWorkspaceNavigationHidden();
  const workspaceNavigationExpanded =
    !_workspaceNavigationHidden && _workspaceNavigationExpanded;

  useDocumentTitle(
    intl.formatMessage({
      id: 'templates.directory.power-ups',
      defaultMessage: 'Power-Ups',
      description: 'Title for the Power-Ups directory',
    }),
  );

  useEffect(() => {
    // eslint-disable-next-line @trello/no-query-selector
    const contentContainer = document.querySelector('#content');
    if (contentContainer) {
      contentContainer.scrollTop = 0;
    }
  }, [section, subsection, query]);

  if (error) {
    return null;
  }

  if (loading || loadingCategories || loadingMemberLocale) {
    return <LoadingSpinner />;
  }

  const isSearchPage = section?.includes('search');
  const isHomePage = typeof section === 'undefined' || section === null;
  const isListingPage = section && /([a-fA-F0-9]{24})/.test(section);
  const isEnablePage = subsection === 'enable';

  let pageContent;

  const publicPlugins = data?.publicPlugins || [];
  const filteredPublicPlugins = filterUnavailablePlugins(publicPlugins, {
    filterDeprecated: isHomePage,
  });

  const categories = categoriesQuery?.pluginCategories || [];

  const categoryData = categories
    .map((categoryKey: string) => {
      const categoryMessage = getCategoryMessage(categoryKey);
      if (!categoryMessage) {
        return null;
      }

      return {
        key: categoryKey,
        url: `category/${categoryKey}`,
        title: categoryMessage,
      };
    })
    .filter((category) => category !== null);

  if (isSearchPage) {
    if (!query?.length) {
      navigate('/power-ups', { trigger: true });

      return null;
    }
    pageContent = <SearchPage plugins={filteredPublicPlugins} query={query!} />;
  } else if (isHomePage) {
    pageContent = <HomePage plugins={filteredPublicPlugins} />;
  } else if (isListingPage) {
    const plugin = getPluginById(filteredPublicPlugins, section);

    if (!plugin) {
      if (section === BUTLER_POWER_UP_ID) {
        window.location.href = BUTLER_HELP_URL;
        return null;
      }
      navigate('/power-ups', { trigger: true });
      return <LoadingSpinner />;
    }

    if (isEnablePage) {
      if (!isMemberLoggedIn()) {
        const returnUrl = escapeReturnUrl(location.href);
        window.location.href = `/login?returnUrl=${returnUrl}`;

        return null;
      }

      pageContent = <EnablePage plugin={plugin} />;
    } else {
      pageContent = <ListingPage plugin={plugin} />;
    }
  } else {
    switch (section) {
      case 'all':
        pageContent = <AllPage plugins={filteredPublicPlugins} />;
        break;
      case 'made-by-trello':
        pageContent = (
          <MadeByTrelloPage
            plugins={getPluginsByTag(filteredPublicPlugins, 'made-by-trello')}
          />
        );
        break;
      case 'category':
        if (!subsection || !isValidCategory(subsection, categories)) {
          navigate('/power-ups', { trigger: true });
          return <LoadingSpinner />;
        }
        pageContent = (
          <CategoryPage
            plugins={getPluginsByCategory(filteredPublicPlugins, subsection)}
            idCategory={subsection}
          />
        );
        break;
      default:
        pageContent = <AllPage plugins={filteredPublicPlugins} />;
        navigate('/power-ups', { trigger: true });
        return null;
    }
  }

  return (
    <div className={styles.directoryBody}>
      <div
        data-testid={getTestId<PowerUpTestIds>('pup-directory')}
        className={classNames(styles.navigationAndContent, {
          [styles.workspaceNavigationExpanded]: workspaceNavigationExpanded,
        })}
      >
        {!isListingPage && (
          <Navigation
            categories={categoryData}
            navigationExpanded={navigationExpanded}
            setNavigationExpanded={setNavigationExpanded}
            urlSlug="power-ups"
            analyticsFn={(key) => {
              Analytics.sendClickedLinkEvent({
                linkName: 'categoryNavLink',
                source: 'publicDirectoryScreen',
                attributes: {
                  category: key,
                },
              });
            }}
            viewAllIcon={<PowerUpIcon />}
            boardIcon={<BoardIcon />}
            SearchComponent={<SearchWrapper />}
            section={section}
            subsection={subsection}
          >
            <NavigationFooter />
          </Navigation>
        )}
        <div
          className={classNames(styles.directoryContent, {
            [styles.navigationExpanded]: navigationExpanded,
            [styles.workspaceNavigationExpanded]: workspaceNavigationExpanded,
          })}
        >
          {pageContent}
        </div>
      </div>
      <Footer />
    </div>
  );
};
